import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { matchPath, NavLink, useLocation } from 'react-router-dom';
import styles from './NavBar.module.css';
export function NavBar({ routes }) {
    const { pathname } = useLocation();
    const [openSubmenuIndex, setOpenSubmenuIndex] = React.useState(null);
    const menuRefs = React.useRef([]);
    const visibleItems = React.useMemo(() => routes.filter(item => item.enabled !== false), [routes]);
    const isParentActive = React.useCallback((item) => {
        return !!matchPath({ path: `${item.route}/*`, end: false }, pathname);
    }, [pathname]);
    const isRouteActive = React.useCallback((route, end) => !!matchPath({ path: route, end }, pathname), [pathname]);
    const linkClassForTopItem = React.useCallback((isActive, parentActive, hasSubmenu) => {
        const shouldHighlight = hasSubmenu ? parentActive : isActive;
        return shouldHighlight ? `${styles.navLink} ${styles.activeTab}` : styles.navLink;
    }, []);
    const openSubmenu = (index) => {
        setOpenSubmenuIndex(index);
        requestAnimationFrame(() => menuRefs.current[index]?.focus());
    };
    const closeSubmenu = () => setOpenSubmenuIndex(null);
    const isSubmenuOpen = (index) => openSubmenuIndex === index;
    return (_jsx("nav", { className: styles.navBar, "data-testid": 'nav-bar', children: visibleItems.map((item, index) => {
            const visibleSubmenu = item.submenu?.filter(sub => sub.enabled !== false) ?? [];
            const shouldCollapseToSingleItem = item.collapseSingleSubmenuItem && visibleSubmenu.length === 1;
            const hasSubmenu = visibleSubmenu.length > 1 || (visibleSubmenu.length === 1 && !shouldCollapseToSingleItem);
            const linkTarget = shouldCollapseToSingleItem ? visibleSubmenu[0].route : item.route;
            const linkExactMatch = shouldCollapseToSingleItem ? visibleSubmenu[0].exactMatch : item.exactMatch;
            const parentActive = isParentActive(item);
            const topItemActive = isRouteActive(linkTarget, linkExactMatch ?? true);
            return (_jsxs("div", { className: styles.navItem, role: 'none', children: [_jsx(NavLink, { to: linkTarget, end: linkExactMatch ?? true, "aria-haspopup": hasSubmenu ? 'menu' : undefined, "aria-expanded": hasSubmenu ? isSubmenuOpen(index) : undefined, "aria-controls": hasSubmenu ? `submenu-${index}` : undefined, "aria-current": topItemActive ? 'page' : undefined, className: linkClassForTopItem(topItemActive, parentActive, hasSubmenu), "data-testid": `nav-bar-link-${index}`, onMouseEnter: () => (hasSubmenu ? openSubmenu(index) : closeSubmenu()), onFocus: hasSubmenu ? () => openSubmenu(index) : undefined, onKeyDown: hasSubmenu
                            ? e => {
                                if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    openSubmenu(index);
                                }
                                if (e.key === 'Escape') {
                                    e.preventDefault();
                                    closeSubmenu();
                                }
                            }
                            : undefined, children: item.title }), hasSubmenu && (_jsx("div", { id: `submenu-${index}`, role: 'menu', tabIndex: -1, "aria-label": item.title, ref: el => {
                            menuRefs.current[index] = el;
                        }, className: isSubmenuOpen(index) ? `${styles.submenu} ${styles.open}` : styles.submenu, onMouseLeave: closeSubmenu, onKeyDown: e => {
                            if (e.key === 'Escape') {
                                e.preventDefault();
                                closeSubmenu();
                            }
                        }, onBlur: e => {
                            if (!e.currentTarget.contains(e.relatedTarget))
                                closeSubmenu();
                        }, children: visibleSubmenu.map(sub => {
                            const subItemActive = isRouteActive(sub.route, sub.exactMatch ?? true);
                            return (_jsx("div", { role: 'none', children: _jsx(NavLink, { to: sub.route, end: sub.exactMatch ?? true, role: 'menuitem', tabIndex: -1, "aria-current": subItemActive ? 'page' : undefined, className: subItemActive ? `${styles.submenuItem} ${styles.submenuItemActive}` : styles.submenuItem, onClick: closeSubmenu, children: sub.title }) }, sub.title));
                        }) }))] }, item.title));
        }) }));
}
export default NavBar;
